import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  MessageSearchResultItem,
  searchMessages,
} from '../utils/dashboardApi';
import { buildCsvContent, downloadCsv } from '../utils/exportUtils';
import { buildSearchResultsCsvSpec } from '../utils/exportConfigs';

const SEARCH_LIMIT = 99999;

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  keyword: string;
  startDate?: string | null;
  endDate?: string | null;
}

const SearchDialog: React.FC<SearchDialogProps> = ({
  open,
  onClose,
  keyword,
  startDate,
  endDate,
}) => {
  const [results, setResults] = useState<MessageSearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<MessageSearchResultItem | null>(null);
  const normalizedKeyword = useMemo(() => keyword.trim(), [keyword]);
  const [totalFound, setTotalFound] = useState<number | null>(null);
  const canExportResults = normalizedKeyword.length > 0 && results.length > 0 && !loading && !error;

  const handleExport = () => {
    if (!canExportResults) {
      return;
    }
    const spec = buildSearchResultsCsvSpec(results, normalizedKeyword);
    const csv = buildCsvContent(spec.headers, spec.rows);
    const startLabel = (startDate ?? 'start').replace(/:/g, '-');
    const endLabel = (endDate ?? 'end').replace(/:/g, '-');
    const keywordLabel = normalizedKeyword.replace(/\s+/g, '_');
    downloadCsv(csv, `search_${keywordLabel}_${startLabel}_to_${endLabel}.csv`);
  };

  useEffect(() => {
    if (!open) {
      setResults([]);
      setError(null);
      setLoading(false);
      setSelectedMessage(null);
      setTotalFound(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!normalizedKeyword) {
      setResults([]);
      setSelectedMessage(null);
      setError('Please enter a keyword to search.');
      setLoading(false);
      setTotalFound(null);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSelectedMessage(null);
    setTotalFound(null);

    searchMessages({
      keyword: normalizedKeyword,
      limit: SEARCH_LIMIT,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      signal: controller.signal,
    })
      .then((response) => {
        if (!isMounted) return;
        setResults(response.data.results ?? []);
        setTotalFound(
          typeof response.data.total_found === 'number' ? response.data.total_found : null
        );
      })
      .catch((err) => {
        if ((err as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to search messages:', err);
        if (isMounted) {
          setResults([]);
          setTotalFound(null);
          setError('Failed to fetch search results.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [open, normalizedKeyword, startDate, endDate]);

  const content = () => {
    if (!normalizedKeyword) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Enter a keyword to search.
        </div>
      );
    }
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Searching messages...
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex h-full items-center justify-center text-center text-sm text-red-500">
          {error}
        </div>
      );
    }
    if (results.length === 0) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-400">
          No messages match “{normalizedKeyword}”.
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-[300px] flex-col">
        <div className="flex items-center px-1 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span className="flex-1">Question</span>
          <span className="w-32 text-right">User</span>
          <span className="w-28 text-right">Time</span>
        </div>
        <div
          className="custom-scrollbar flex-1 min-h-0 overflow-y-auto pr-2"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}
        >
          {results.map((row) => {
            return (
              <div
                key={row.message_id}
                className="border-t border-gray-100 px-1 py-3 text-sm text-gray-700"
              >
                <div className="flex items-start gap-4">
                  <Tooltip title={`Message ID: ${row.message_id}`} arrow>
                    <button
                      type="button"
                      className="flex-1 text-left font-medium text-gray-900 hover:text-indigo-600"
                      onClick={() => setSelectedMessage(row)}
                    >
                      {row.question || '--'}
                    </button>
                  </Tooltip>
                  <Tooltip title={`User ID: ${row.user_id}`} arrow>
                    <span className="w-32 text-right font-medium text-gray-900">
                      {row.username}
                    </span>
                  </Tooltip>
                  <span className="w-28 text-right text-xs text-gray-500">
                    {formatDateLabel(row.question_timestamp)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleDialogClose = (
    _: object,
    __?: 'backdropClick' | 'escapeKeyDown'
  ) => {
    setSelectedMessage(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
        <DialogTitle>
          <div className="flex items-center justify-between gap-3">
            <span>{renderDialogTitle(normalizedKeyword, totalFound)}</span>
            <Tooltip title="Export to CSV">
              <span>
                <IconButton size="small" onClick={handleExport} disabled={!canExportResults}>
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </DialogTitle>
        <DialogContent>{content()}</DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={Boolean(selectedMessage)}
        onClose={() => setSelectedMessage(null)}
        hideBackdrop
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
          pointerEvents: 'none',
        }}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
        }}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 380 }, pointerEvents: 'auto', px: 0 },
        }}
      >
        {selectedMessage && (
          <DrawerContent message={selectedMessage} onClose={() => setSelectedMessage(null)} />
        )}
      </Drawer>
    </>
  );
};

export default SearchDialog;

const DrawerContent: React.FC<{
  message: MessageSearchResultItem;
  onClose: () => void;
}> = ({ message, onClose }) => (
  <div className="flex h-full w-[360px] flex-col">
    <div className="flex items-center justify-between border-b border-gray-200 pl-4 pr-[6px] py-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Message Detail</p>
      </div>
      <IconButton size="small" onClick={onClose} sx={{ mr: -0.5 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm text-gray-700">
      <InfoSection title="Question" body={message.question || '--'} highlight />
      <InfoSection title="Answer" body={message.answer || 'No answer available.'} allowPreWrap />
      <InfoSection title="User" body={message.username || '--'} />
      <InfoSection title="User ID" body={String(message.user_id ?? '--')} />
      <InfoSection title="Message ID" body={message.message_id || '--'} allowBreak />
      <InfoSection title="Time" body={formatDateTimeLabel(message.question_timestamp)} />
    </div>
  </div>
);

const InfoSection: React.FC<{
  title: string;
  body: string;
  highlight?: boolean;
  allowPreWrap?: boolean;
  allowBreak?: boolean;
}> = ({ title, body, highlight = false, allowPreWrap = false, allowBreak = false }) => (
  <div className="border-t border-blue-100 first:border-t-0 first:pt-0 first:mt-0 pt-3 mt-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">{title}</p>
    <p
      className={`mt-1 text-sm ${highlight ? 'text-gray-900' : 'text-gray-700'} ${
        allowPreWrap ? 'whitespace-pre-wrap' : ''
      } ${allowBreak ? 'break-all' : ''}`}
    >
      {body}
    </p>
  </div>
);

const formatDateLabel = (timestamp?: string | null): string => {
  if (!timestamp) return '--';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toISOString().slice(0, 10);
};

const formatDateTimeLabel = (timestamp?: string | null): string => {
  if (!timestamp) return '--';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const renderDialogTitle = (keyword: string, totalFound: number | null): string => {
  if (!keyword) {
    return 'Search Results';
  }
  const countLabel = totalFound !== null ? totalFound : 0;
  return `${countLabel} messages related to "${keyword}"`;
};
